import { ArrowLeft, CheckCircle2, KeyRound, MailCheck, MapPinCheck, UsersRound } from 'lucide-react';
import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { BrandLogo } from '../../components/BrandLogo';
import { InlineSpinner } from '../../components/LoadingIndicators';
import { ThemeToggle } from '../../components/ThemeToggle';
import { apiRequest, getApiErrorMessage } from '../../lib/api';

const requestMessage = 'If an active account matches that email, a password reset link will be sent.';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await apiRequest('/auth/password-reset/request/', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim() }),
      });
      setSent(true);
    } catch (cause) {
      setError(getApiErrorMessage(cause));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <RecoveryFrame>
      {sent ? (
        <RecoveryResult
          icon={<MailCheck size={44} />}
          title="Check your email"
          message={requestMessage}
        />
      ) : (
        <form className="login-form" onSubmit={(event) => { void submit(event); }}>
          <Link className="auth-back-link" to="/"><ArrowLeft size={15} /> Back to sign in</Link>
          <p className="eyebrow">Account recovery</p>
          <h2>Reset your password</h2>
          <p className="muted">Enter your sign-in email. If it matches an active account, we will send a one-time link.</p>
          <label>
            Email address
            <input
              autoComplete="email"
              autoFocus
              inputMode="email"
              onChange={(event) => setEmail(event.target.value)}
              placeholder="name@company.com"
              required
              type="email"
              value={email}
            />
          </label>
          {error ? <p className="form-error" role="alert">{error}</p> : null}
          <button className="button primary full" disabled={submitting} type="submit">
            {submitting ? <InlineSpinner /> : <MailCheck size={17} />} {submitting ? 'Sending…' : 'Send reset link'}
          </button>
        </form>
      )}
    </RecoveryFrame>
  );
}

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const { signOut } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const user = searchParams.get('user') ?? '';
  const token = searchParams.get('token') ?? '';
  const hasLink = Boolean(user && token);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (password !== confirmation) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await apiRequest('/auth/password-reset/confirm/', {
        method: 'POST',
        body: JSON.stringify({ user, token, new_password: password }),
      });
      signOut();
      setComplete(true);
    } catch (cause) {
      setError(getApiErrorMessage(cause));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <RecoveryFrame>
      {complete ? (
        <RecoveryResult
          icon={<CheckCircle2 size={44} />}
          title="Password updated"
          message="Your password has been reset. Sign in again on web or mobile with your new password."
        />
      ) : !hasLink ? (
        <RecoveryResult
          icon={<KeyRound size={44} />}
          title="Reset link unavailable"
          message="This password reset link is incomplete. Request a new link and try again."
          action={<Link className="button primary" to="/forgot-password">Request a new link</Link>}
        />
      ) : (
        <form className="login-form" onSubmit={(event) => { void submit(event); }}>
          <Link className="auth-back-link" to="/"><ArrowLeft size={15} /> Back to sign in</Link>
          <p className="eyebrow">Secure your account</p>
          <h2>Choose a new password</h2>
          <p className="muted">Use at least eight characters and avoid a common or easily guessed password.</p>
          <label>
            New password
            <input
              autoComplete="new-password"
              autoFocus
              minLength={8}
              onChange={(event) => setPassword(event.target.value)}
              required
              type="password"
              value={password}
            />
          </label>
          <label>
            Confirm new password
            <input
              autoComplete="new-password"
              minLength={8}
              onChange={(event) => setConfirmation(event.target.value)}
              required
              type="password"
              value={confirmation}
            />
          </label>
          {error ? <p className="form-error" role="alert">{error}</p> : null}
          <button className="button primary full" disabled={submitting} type="submit">
            {submitting ? <InlineSpinner /> : <KeyRound size={17} />} {submitting ? 'Updating…' : 'Update password'}
          </button>
        </form>
      )}
    </RecoveryFrame>
  );
}

export function RecoveryResult({
  icon,
  title,
  message,
  action = <Link className="button primary" to="/">Go to sign in</Link>,
}: {
  icon: ReactNode;
  title: string;
  message: string;
  action?: ReactNode;
}) {
  return (
    <section className="login-form recovery-result">
      <span className="recovery-result-icon">{icon}</span>
      <h2>{title}</h2>
      <p className="muted">{message}</p>
      {action}
    </section>
  );
}

export function RecoveryFrame({ children }: { children: ReactNode }) {
  return (
    <main className="login-page">
      <ThemeToggle className="auth-theme-toggle" />
      <section className="login-story">
        <BrandLogo className="brand-logo-auth" variant="lockup" />
        <div>
          <p className="eyebrow">Secure account recovery</p>
          <h1>Back to your work.<br /><span>Without shortcuts.</span></h1>
          <p className="story-copy">A short-lived, one-time link keeps your account protected while you choose a new password.</p>
        </div>
        <div className="story-points">
          <span><UsersRound size={18} /> One account across BFC</span>
          <span><MapPinCheck size={18} /> Existing sessions revoked</span>
        </div>
      </section>
      <section className="login-panel">{children}</section>
    </main>
  );
}
